import { useEffect, useState, useCallback } from "react";
import { Plus, Search, Pencil, Trash2, Package, X, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import api, { fmtINR, errMsg, fileUrl } from "../../lib/api";
import { PageHeader, StatusBadge, TableShell, TR, TD, LoadingRows, EmptyState, ConfirmDialog } from "../../components/Shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const EMPTY = { name: "", description: "", price: "", compare_price: "", sku: "", stock: 0, category: "General", images: [], status: "active", product_type: "simple", attributes: [], variants: [] };

export default function StoreProducts() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [optLabel, setOptLabel] = useState("");
  const [optRows, setOptRows] = useState([]);
  const [legacyOpts, setLegacyOpts] = useState(false);
  const addOptRow = () => setOptRows((r) => [...r, { value: "", price: form.price === "" ? "" : form.price, stock: form.stock, image: "" }]);
  const setOptRow = (i, k, v) => setOptRows((r) => r.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const delOptRow = (i) => setOptRows((r) => r.filter((_, j) => j !== i));
  const clearOpts = () => { setOptLabel(""); setOptRows([]); };
  const [imgUrl, setImgUrl] = useState("");
  const [addingCat, setAddingCat] = useState(false);
  const [newCat, setNewCat] = useState("");
  const [imgBusy, setImgBusy] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/store-admin/products", { params: { q, category } }).then((r) => setData(r.data));
  }, [q, category]);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    let payload = { ...form, price: Number(form.price), compare_price: form.compare_price ? Number(form.compare_price) : null, stock: Number(form.stock) };
    if (!legacyOpts) {
      const rows = optRows.filter((r) => r.value.trim());
      const label = optLabel.trim();
      if (label && rows.length) {
        payload = { ...payload, product_type: "variants",
          attributes: [{ name: label, options: rows.map((r) => r.value.trim()) }],
          variants: rows.map((r) => ({ combo: { [label]: r.value.trim() }, price: Number(r.price) || 0, sale_price: "", sku: "", stock: Number(r.stock) || 0, image: r.image || "", status: "active" })) };
      } else {
        payload = { ...payload, product_type: "simple", attributes: [], variants: [] };
      }
    }
    try {
      if (dialog.mode === "create") await api.post("/store-admin/products", payload);
      else await api.patch(`/store-admin/products/${dialog.product.id}`, payload);
      toast.success("Product saved"); setDialog(null); load();
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };

  const remove = async (p) => {
    await api.delete(`/store-admin/products/${p.id}`);
    toast.success("Product deleted"); load();
  };

  const openEdit = (p) => {
    setForm({ ...p, compare_price: p.compare_price || "" });
    const attrs = p.attributes || [];
    if (p.product_type === "variants" && attrs.length === 1 && (p.variants || []).length) {
      const a = attrs[0];
      setOptLabel(a.name);
      setOptRows(p.variants.map((v) => ({ value: (v.combo || {})[a.name] || "", price: v.price ?? "", stock: v.stock ?? 0, image: v.image || "" })));
      setLegacyOpts(false);
    } else if (p.product_type === "variants" && (p.variants || []).length) {
      setLegacyOpts(true); clearOpts();
    } else {
      setLegacyOpts(false); clearOpts();
    }
    setImgUrl("");
    setAddingCat(false);
    setDialog({ mode: "edit", product: p });
  };

  const addCategory = async () => {
    const name = newCat.trim();
    if (!name) return;
    try {
      await api.post("/store-admin/categories", { name });
      toast.success(`Category "${name}" added`);
      setForm((f) => ({ ...f, category: name }));
      setNewCat(""); setAddingCat(false);
      load();
    } catch (e2) { toast.error(errMsg(e2)); }
  };

  const uploadImg = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (form.images.length >= 4) { toast.error("Maximum 4 images per product"); return; }
    setImgBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd);
      setForm((f) => ({ ...f, images: [...f.images, data.url] }));
    } catch (e2) { toast.error(errMsg(e2)); } finally { setImgBusy(false); }
  };

  const addImgUrl = () => {
    const u = imgUrl.trim();
    if (!u) return;
    if (form.images.length >= 4) { toast.error("Maximum 4 images per product"); return; }
    setForm((f) => ({ ...f, images: [...f.images, u] }));
    setImgUrl("");
  };

  return (
    <div data-testid="store-products">
      <PageHeader title="Products" subtitle="Your store catalogue and inventory.">
        <Button data-testid="add-product-button" onClick={() => { setForm(EMPTY); clearOpts(); setLegacyOpts(false); setImgUrl(""); setDialog({ mode: "create" }); }} className="rounded-full font-bold active:scale-95 transition-transform">
          <Plus className="h-4 w-4 mr-2" /> Add Product
        </Button>
      </PageHeader>

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative max-w-sm flex-1 min-w-52">
          <Search className="h-4 w-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="product-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products…" className="pl-10 h-11 rounded-full" />
        </div>
        <div className="flex gap-2 flex-wrap">
          <button data-testid="pcat-all" onClick={() => setCategory("")} className={`px-4 h-11 rounded-full text-sm font-bold border transition-colors ${!category ? "bg-primary text-primary-foreground border-transparent" : "border-border"}`}>All</button>
          {(data?.categories || []).map((c) => (
            <button key={c} data-testid={`pcat-${c.toLowerCase()}`} onClick={() => setCategory(c)} className={`px-4 h-11 rounded-full text-sm font-bold border transition-colors ${category === c ? "bg-primary text-primary-foreground border-transparent" : "border-border"}`}>{c}</button>
          ))}
        </div>
      </div>

      {!data ? <LoadingRows /> : data.products.length === 0 ? (
        <EmptyState icon={Package} title="No products" body="Add your first product to start selling." />
      ) : (
        <TableShell testid="products-table" head={["Product", "SKU", "Category", "Price", "Stock", "Status", "Actions"]}>
          {data.products.map((p) => (
            <TR key={p.id} testid={`product-row-${p.id}`}>
              <TD>
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 rounded-md bg-muted overflow-hidden shrink-0">
                    {p.images?.[0] && <img src={p.images[0]} alt="" className="h-full w-full object-cover" />}
                  </div>
                  <span className="font-bold">{p.name}</span>
                </div>
              </TD>
              <TD className="font-mono text-xs">{p.sku || "—"}</TD>
              <TD className="text-sm">{p.category}</TD>
              <TD><span className="font-bold">{fmtINR(p.price)}</span>{p.compare_price > p.price && <span className="text-xs text-muted-foreground line-through ml-2">{fmtINR(p.compare_price)}</span>}</TD>
              <TD>
                <span data-testid={`stock-${p.id}`} className={`font-bold ${p.stock <= 0 ? "text-destructive" : p.stock < 10 ? "text-amber-600" : ""}`}>{p.stock}</span>
                {p.stock > 0 && p.stock < 10 && <span className="text-xs text-amber-600 ml-1.5">low</span>}
              </TD>
              <TD><StatusBadge status={p.status} /></TD>
              <TD>
                <div className="flex gap-1">
                  <button data-testid={`edit-product-${p.id}`} onClick={() => openEdit(p)} className="h-8 w-8 rounded-full hover:bg-muted flex items-center justify-center transition-colors"><Pencil className="h-4 w-4" /></button>
                  <ConfirmDialog testid={`delete-product-${p.id}`} title="Delete product?" description={`"${p.name}" will be permanently removed.`}
                    onConfirm={() => remove(p)}
                    trigger={<button className="h-8 w-8 rounded-full hover:bg-destructive/10 text-destructive flex items-center justify-center transition-colors"><Trash2 className="h-4 w-4" /></button>} />
                </div>
              </TD>
            </TR>
          ))}
        </TableShell>
      )}

      <Dialog open={!!dialog} onOpenChange={() => setDialog(null)}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{dialog?.mode === "create" ? "Add Product" : "Edit Product"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4 mt-2" data-testid="product-form">
            <div className="space-y-1.5"><Label className="overline-label">Name</Label>
              <Input data-testid="product-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label className="overline-label">Description</Label>
              <Textarea data-testid="product-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="overline-label">Price (₹)</Label>
                <Input data-testid="product-price" type="number" required value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} /></div>
              <div className="space-y-1.5"><Label className="overline-label">Compare price</Label>
                <Input data-testid="product-compare-price" type="number" value={form.compare_price} onChange={(e) => setForm({ ...form, compare_price: e.target.value })} /></div>
              <div className="space-y-1.5"><Label className="overline-label">SKU</Label>
                <Input data-testid="product-sku" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} /></div>
              <div className="space-y-1.5"><Label className="overline-label">Stock</Label>
                <Input data-testid="product-stock" type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label className="overline-label">Category</Label>
              {!addingCat ? (
                <Select value={form.category} onValueChange={(v) => (v === "__new__" ? setAddingCat(true) : setForm({ ...form, category: v }))}>
                  <SelectTrigger data-testid="product-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    {(data?.categories || []).map((c) => (
                      <SelectItem key={c} value={c} data-testid={`cat-option-${c.toLowerCase().replace(/\s+/g, "-")}`}>{c}</SelectItem>
                    ))}
                    <SelectItem value="__new__" data-testid="cat-add-new" className="font-bold text-primary">+ Add New Category</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <div className="flex gap-2">
                  <Input data-testid="new-category-input" value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="New category name" autoFocus />
                  <Button type="button" data-testid="create-category-button" onClick={addCategory} variant="outline" className="rounded-full font-bold shrink-0">Add</Button>
                  <Button type="button" data-testid="cancel-category-button" variant="ghost" onClick={() => { setAddingCat(false); setNewCat(""); }} className="rounded-full shrink-0">Cancel</Button>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label className="overline-label">Product Options (optional)</Label>
              {legacyOpts ? (
                <p className="text-xs text-muted-foreground border border-border rounded-md p-3" data-testid="legacy-options-note">This product uses advanced option combinations created earlier. They are kept as-is and continue to work on your storefront.</p>
              ) : (
                <>
                  <p className="text-xs text-muted-foreground">Sell this product in options like Weight, Color or Size — each with its own price and stock.</p>
                  {optRows.length > 0 && (
                    <div className="border border-border rounded-md p-3 space-y-2.5" data-testid="options-builder">
                      <Input data-testid="option-group-name" value={optLabel} onChange={(e) => setOptLabel(e.target.value)} placeholder="Option name — e.g. Weight, Color, Size" className="h-9 text-sm font-bold" />
                      {optRows.map((r, i) => (
                        <div key={i} className="space-y-1.5" data-testid={`option-row-${i}`}>
                          <div className="flex gap-2 items-center">
                            <Input value={r.value} onChange={(e) => setOptRow(i, "value", e.target.value)} placeholder="e.g. 100 g" className="h-9 text-sm flex-1 min-w-0" data-testid={`option-value-${i}`} />
                            <Input type="number" value={r.price} onChange={(e) => setOptRow(i, "price", e.target.value)} placeholder="₹ Price" className="h-9 text-sm w-24 shrink-0" data-testid={`option-price-${i}`} />
                            <Input type="number" value={r.stock} onChange={(e) => setOptRow(i, "stock", e.target.value)} placeholder="Stock" className="h-9 text-sm w-20 shrink-0" data-testid={`option-stock-${i}`} />
                            <button type="button" data-testid={`option-remove-${i}`} onClick={() => delOptRow(i)} className="h-8 w-8 shrink-0 rounded-full hover:bg-destructive/10 text-destructive flex items-center justify-center transition-colors"><X className="h-4 w-4" /></button>
                          </div>
                          {form.images.length > 0 && (
                            <select value={r.image} onChange={(e) => setOptRow(i, "image", e.target.value)} data-testid={`option-image-${i}`}
                              className="h-8 w-full text-xs rounded-md border border-border bg-background px-2">
                              <option value="">Default product image</option>
                              {form.images.map((src, j) => <option key={src} value={src}>Image {j + 1}{j === 0 ? " (main)" : ""}</option>)}
                            </select>
                          )}
                        </div>
                      ))}
                      {optRows.some((r) => Number(r.stock) <= 0) && <p className="text-[11px] text-amber-600 font-bold">Options with stock 0 will show as "Out of Stock" to customers.</p>}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button type="button" data-testid="add-option-button" variant="outline" onClick={addOptRow} className="rounded-full font-bold text-xs">
                      <Plus className="h-3.5 w-3.5 mr-1" /> Add Option
                    </Button>
                    {(optRows.length > 0 || optLabel) && (
                      <Button type="button" data-testid="clear-options-button" variant="ghost" onClick={clearOpts} className="rounded-full font-bold text-xs text-muted-foreground">Remove options</Button>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="space-y-1.5"><Label className="overline-label">Product Images (up to 4 — first is the main image)</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {form.images.map((src, i) => (
                  <div key={src} className="relative aspect-square rounded-md overflow-hidden border border-border group" data-testid={`img-preview-${i}`}>
                    <img src={fileUrl(src)} alt="" className="h-full w-full object-cover" />
                    {i === 0 && <span className="absolute bottom-1 left-1 text-[9px] font-extrabold bg-black/70 text-white px-1.5 py-0.5 rounded">MAIN</span>}
                    <button type="button" data-testid={`img-remove-${i}`} onClick={() => setForm((f) => ({ ...f, images: f.images.filter((_, j) => j !== i) }))}
                      className="absolute top-1 right-1 h-5 w-5 rounded-full bg-black/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><X className="h-3 w-3" /></button>
                  </div>
                ))}
                {form.images.length < 4 && (
                  <label data-testid="img-upload-button" className="aspect-square rounded-md border-2 border-dashed border-border flex flex-col items-center justify-center cursor-pointer hover:bg-muted/50 transition-colors text-muted-foreground">
                    <ImagePlus className="h-5 w-5" />
                    <span className="text-[10px] font-bold mt-1">{imgBusy ? "Uploading…" : "Upload"}</span>
                    <input type="file" accept="image/*" className="hidden" onChange={uploadImg} disabled={imgBusy} />
                  </label>
                )}
              </div>
              <div className="flex gap-2">
                <Input data-testid="product-image-url" value={imgUrl} onChange={(e) => setImgUrl(e.target.value)} placeholder="…or paste an image URL" />
                <Button type="button" data-testid="img-add-url" variant="outline" onClick={addImgUrl} className="rounded-full font-bold shrink-0">Add</Button>
              </div>
            </div>
            <Button data-testid="product-save-button" disabled={busy} className="w-full h-11 rounded-full font-bold">{busy ? "Saving…" : "Save Product"}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
